const POST_AUTH_REDIRECT_STORAGE_KEY = 'motito_post_auth_redirect_v1';
const POST_AUTH_REDIRECT_TTL_MS = 30 * 60 * 1000;
const REDIRECT_VALIDATION_ORIGIN = 'https://motito.local';

const BLOCKED_REDIRECT_PREFIXES = [
  '/sign-in',
  '/sign-up',
  '/logout',
  '/auth',
  '/api',
  '/campaign/bootstrap',
  '/onboarding/complete-profile',
] as const;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type StoredPostAuthRedirect = {
  path: string;
  expiresAt: number;
};

function getStorage(storage?: StorageLike): StorageLike | null {
  if (storage) {
    return storage;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isBlockedRedirectPath(pathname: string): boolean {
  return BLOCKED_REDIRECT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function normalizePathnameForChecks(pathname: string): string | null {
  try {
    const decodedPathname = decodeURIComponent(pathname).replace(/\\/g, '/');
    // URL parsers strip ASCII controls; a decoded tab between slashes can
    // otherwise turn a local path into an external protocol-relative URL.
    if (
      /\p{Cc}/u.test(decodedPathname) ||
      !decodedPathname.startsWith('/') ||
      decodedPathname.startsWith('//')
    ) {
      return null;
    }
    return decodedPathname;
  } catch {
    return null;
  }
}

function clearStoredPostAuthRedirect(storage: StorageLike | null): void {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(POST_AUTH_REDIRECT_STORAGE_KEY);
  } catch {
    // Storage access is best-effort.
  }
}

function readStoredPostAuthRedirect(
  storage?: StorageLike
): StoredPostAuthRedirect | null {
  const resolvedStorage = getStorage(storage);
  if (!resolvedStorage) {
    return null;
  }

  try {
    const raw = resolvedStorage.getItem(POST_AUTH_REDIRECT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredPostAuthRedirect>;
    const path = safeRedirectPath(
      typeof parsed.path === 'string' ? parsed.path : null
    );
    const expiresAt =
      typeof parsed.expiresAt === 'number' ? parsed.expiresAt : Number.NaN;

    if (!path || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      clearStoredPostAuthRedirect(resolvedStorage);
      return null;
    }

    return {
      path,
      expiresAt,
    };
  } catch {
    clearStoredPostAuthRedirect(resolvedStorage);
    return null;
  }
}

function canonicalizeRedirectPathOnce(
  candidate: string | null | undefined
): string | null {
  if (typeof candidate !== 'string') {
    return null;
  }

  const trimmed = candidate.trim();
  if (!trimmed || !trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return null;
  }

  try {
    const parsed = new URL(trimmed, REDIRECT_VALIDATION_ORIGIN);
    const normalizedPathname = normalizePathnameForChecks(parsed.pathname);
    if (!normalizedPathname || isBlockedRedirectPath(normalizedPathname)) {
      return null;
    }

    const destination = new URL(
      `${normalizedPathname}${parsed.search}${parsed.hash}`,
      REDIRECT_VALIDATION_ORIGIN
    );
    const finalPathname = normalizePathnameForChecks(destination.pathname);
    if (
      destination.origin !== REDIRECT_VALIDATION_ORIGIN ||
      !finalPathname ||
      isBlockedRedirectPath(finalPathname)
    ) {
      return null;
    }
    return `${destination.pathname}${destination.search}${destination.hash}`;
  } catch {
    return null;
  }
}

export function safeRedirectPath(
  candidate: string | null | undefined
): string | null {
  const canonicalPath = canonicalizeRedirectPathOnce(candidate);
  if (!canonicalPath) {
    return null;
  }

  return canonicalizeRedirectPathOnce(canonicalPath) === canonicalPath
    ? canonicalPath
    : null;
}

export function storePostAuthRedirect(
  path: string,
  storage?: StorageLike
): void {
  const resolvedStorage = getStorage(storage);
  const safePath = safeRedirectPath(path);

  if (!resolvedStorage || !safePath) {
    return;
  }

  try {
    resolvedStorage.setItem(
      POST_AUTH_REDIRECT_STORAGE_KEY,
      JSON.stringify({
        path: safePath,
        expiresAt: Date.now() + POST_AUTH_REDIRECT_TTL_MS,
      } satisfies StoredPostAuthRedirect)
    );
  } catch {
    // Storage access is best-effort.
  }
}

export function peekPostAuthRedirect(storage?: StorageLike): string | null {
  return readStoredPostAuthRedirect(storage)?.path ?? null;
}

export function consumePostAuthRedirect(storage?: StorageLike): string | null {
  const resolvedStorage = getStorage(storage);
  const storedRedirect = readStoredPostAuthRedirect(resolvedStorage ?? undefined);

  if (!storedRedirect) {
    return null;
  }

  clearStoredPostAuthRedirect(resolvedStorage);
  return storedRedirect.path;
}
